<template>
    <div class="container my-2 my-md-2">
        <div class="card p-3 mb-2 shadow-sm filter-card">
            <div class="row g-2 g-md-3">
                <div class="col-12 col-md-4">
                    <label class="form-label">Department</label>
                    <AsyncSelect
                        placeholder="Select Department"
                        v-model="department"
                        :api-service="fetchDepartments"
                        :additional-query="{ company_id: companyId }"
                        label="name"
                        :key="reRenderKey"
                    />
                </div>
                <div class="col-12 col-md-4">
                    <label class="form-label">Doctor</label>
                    <input
                        v-model="search"
                        type="text"
                        class="form-control"
                        placeholder="Search doctor"
                    >
                </div>
                <div class="col-12 col-md-3">
                    <label class="form-label">Date & Time</label>
                    <input
                        v-model="scheduledAt"
                        type="date"
                        class="form-control"
                    >
                </div>
                <div class="col-12 col-md-1 align-content-end">
                    <button
                        class="btn btn-primary waves-effect waves-float waves-light"
                        :disabled="isDisabledSearch || loading"
                        @click="onSearch"
                    >
                        <span v-if="loading">Loading...</span>
                        <span v-else>Search</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- 👨‍⚕️ Doctors -->
        <div class="row g-3 g-md-2">
            <div
                class="col-12 col-sm-12 col-md-6 col-lg-6"
                v-for="(doctor, index) in doctors"
                :key="index"
            >
                <div class="card doctor-card h-100 shadow-sm">
                    <div class="row g-0">
                        <!-- 🖼 Image -->
                        <div class="col-12 col-md-4 doctor-img-wrap text-center">
                            <img
                                :src="doctor.photo"
                                class="img-fluid doctor-img"
                                alt="Doctor"
                            />
                        </div>
                        <!-- 📄 Info -->
                        <div class="col-12 col-md-8">
                            <div class="card-body d-flex flex-column h-100">
                                <div class="doctor-info">
                                    <h6 class="card-title mb-2">
                                        {{ doctor.name }}
                                    </h6>
                                    <p class="mb-1 small short-desc">
                                        {{ doctor.short_description }}
                                    </p>
                                    <p class="mb-1 small">
                                        {{ doctor.department }}
                                    </p>
                                    <p :title="doctor.full_description" class="text-muted small mb-2">
                                        {{ truncateDesc(doctor.full_description, 120) }}
                                    </p>
                                </div>
                                <!-- TODO: Add availability -->
                                <!-- <div class="d-flex justify-content-between small text-muted mt-auto mb-2">
                                    <span>📅 {{ doctor.day }}</span>
                                    <span>⏰ {{ doctor.time }}</span>
                                </div> -->
                                <!-- 🔘 Actions -->
                                <div class="d-flex flex-column flex-sm-row gap-2 card-btn-group">
                                    <button @click="goToProfile(doctor)" class="btn btn-outline-primary w-100">
                                        View Profile
                                    </button>
                                    <button @click="goToProfile(doctor)" class="btn btn-primary w-100">
                                        Appointment
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Loader v-if="loading" />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import handleOnlineAppointment from '@/services/modules/online-appointment/onlineAppointment';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import Loader from '@/components/atom/LoaderComponent.vue';

const { fetchDepartments, fetchServiceResources } = handleOnlineAppointment();

const route = useRoute();

const department = ref(null);
const search = ref('');
const scheduledAt = ref('');
const loading = ref(false);
const doctors = ref([]);
const reRenderKey = ref(0);
const router = useRouter();

const companyId = computed(() => route.query.companyId || '');
const isDisabledSearch = computed(() => !department.value && !search.value && !scheduledAt.value);

const getQuery = () => {
    let query = `?company_id=${companyId.value}`;

    if (department.value) query += `&department_id=${department.value.id}`;
    if (search.value)     query += `&q=${search.value}`;
    if (scheduledAt.value) query += `&scheduled_at=${scheduledAt.value}`;

    return query;
};

const truncateDesc = (text, limit = 50) => {
  if (!text) return '';
  return text?.length > limit ? text.slice(0, limit) + '...' : text
}

const onSearch = async () => {
    const query = getQuery();

    loading.value = true;
    try {
        const res = await fetchServiceResources(query);
        if (!res.status) {
            showError(res.message);
            return;
        }

        doctors.value = res.data;
    } catch (e) {
        showError('Something went wrong during fetching service resources');
    } finally {
        loading.value = false;
    }
};

const goToProfile = (doctor) => {
    router.push({
        name: 'doctor-profile-appointment',
        params: {
            resourceId: doctor.id
        },
        query: {
            companyId: companyId.value
        }
    });
}

const resetComponents = () => {
    doctors.value = [];
    department.value = null;
    search.value = '';
    scheduledAt.value = '';
    reRenderKey.value++;
}

watch(() => route.query.companyId, () => {
    resetComponents();
});
</script>

<style>
.description {
    white-space: pre-line;
}
.short-desc {
  white-space: pre-line;
}
.doctor-info {
    min-height: 230px;
}
.container {
    max-width: 100% !important;
}
.card-body {
    height: 100%;
}
/* Card */
.doctor-card {
    border-radius: 12px;
    transition: 0.2s ease;
}
.doctor-card:hover {
    transform: translateY(-4px);
}
/* Image */
.doctor-img-wrap {
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 140px;
}
.doctor-img {
    object-fit: cover;
    padding: 10px;
    height: 100%;
    width: 100%;
}
/* Text */
.card-title {
    font-weight: 600;
    font-size: 1rem;
}
.description {
    font-size: 0.85rem;
}
/* Buttons */
.card-btn-group .btn {
    border-radius: 8px;
    font-size: 0.9rem;
    padding: 8px;
}
/* Filters */
.filter-card {
    border-radius: 12px;
}
/* 📱 Mobile Optimization */
@media (max-width: 576px) {
    .card-title {
        font-size: 0.95rem;
    }
    .description {
        font-size: 0.8rem;
    }
    .doctor-img-wrap {
        min-height: 120px;
    }
}
/* 📲 Tablet */
@media (min-width: 577px) and (max-width: 991px) {
}
/* 💻 Large screens */
@media (min-width: 1200px) {
    .doctor-card {
        padding: 5px;
    }
}
</style>